import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityCollectionServiceBase, EntityCollectionServiceElementsFactory } from '@ngrx/data';
import { isNil, omitBy } from 'lodash';
import { PagedResponse, QueryPagingParams } from 'mbs-ui-shared';
import { MANUAL_ERROR_HANDLED } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import {
  AzureLifecycleRulesRequest,
  BucketInfo,
  Destination,
  DestinationsResponse,
  GoogleStorageClass,
  PartialSuccessResponse,
  S3LifecycleRulesRequest,
  SetDestinationType,
  SetStorageType,
  StorageAccountLegacy,
  StorageAccountRegion,
  StorageAccountV2,
  StorageAccountV2ListUnit,
  StoragesAvailableForCreation,
  ValidateRequestBody,
  ValidateResponse
} from 'mbs-ui-shared@models/StorageAccount';
import { SwitchCommercial } from 'mbs-ui-shared@models/storage-accounts/SwitchCommercial';
import { UnifiedBillingAccount } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';
import { CompanyType } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.types';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, pluck, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StorageAccountsService extends EntityCollectionServiceBase<StorageAccountLegacy> {
  private readonly api = {
    v1: '/api/StorageAccounts',
    v2: '/api/v2/storageAccounts',
    wizard: '/api/v2/storageAccounts/wizard',
    legacy: '/Admin/StorageAccounts'
  };
  private myLoadedFree$ = new BehaviorSubject<any>(false);
  public loadedFree$ = this.myLoadedFree$.asObservable();

  private myEntitiesFree$ = new BehaviorSubject<any>(null);
  public entitiesFree$ = this.myEntitiesFree$.asObservable();

  private myUnifiedBillings$ = new BehaviorSubject<{
    totalCount: number;
    items: UnifiedBillingAccount[];
  }>({ totalCount: 0, items: [] });
  public unifiedBillings$ = this.myUnifiedBillings$.asObservable();

  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory, private http: HttpClient) {
    super('StorageAccount', serviceElementsFactory);
  }

  public getS3(): Observable<StorageAccountLegacy[]> {
    return this.http.get<StorageAccountLegacy[]>('api/email-service-settings/available-s3-accounts');
  }

  public getCblStorage(): Observable<any> {
    return this.http.get(`${this.api.legacy}/cblStorageExists`).pipe(
      tap((cblStorage) => {
        this.myEntitiesFree$.next(cblStorage);
        this.myLoadedFree$.next(true);
      })
    );
  }

  public getAzureOauthUrl(): Observable<string> {
    return this.http.post<string>(`${this.api.legacy}/GetAzureOAuthURL`, {});
  }

  public switchToCommercial(storageAccountId: string): Observable<SwitchCommercial> {
    return this.http.put<SwitchCommercial>(`${this.api.v1}/${storageAccountId}/SwitchToPaidMode`, null);
  }

  public getMinimumStorageDurationForWasabi(storageAccountId: string): Observable<{ duration: number }> {
    return this.http.get<{ duration: number }>(`${this.api.v1}/${storageAccountId}/MinimumStorageDuration`);
  }

  public minimumStorageDurationForWasabi(storageAccountId: string, duration: number): Observable<any> {
    return this.http.put<any>(`${this.api.v1}/${storageAccountId}/MinimumStorageDuration`, { minimumStorageDuration: duration });
  }

  public getBuyLink(storageAccountId: string): Observable<string> {
    return this.http.get<any>(`${this.api.v1}/${storageAccountId}/SwitchToPaidMode/geturl`).pipe(pluck('url'));
  }

  public getUnifiedBillings(
    params: HttpParams | { [key: string]: string } = { visible: 'true' }
  ): Observable<{ totalCount: number; items: UnifiedBillingAccount[] }> {
    const result$ = new Subject<{ totalCount: number; items: UnifiedBillingAccount[] }>();
    this.http.get<{ totalCount: number; items: UnifiedBillingAccount[] }>(`${this.api.v1}/ub`, { params }).subscribe(
      (data) => result$.next(data),
      (err) => result$.error(err),
      () => result$.complete()
    );

    result$.subscribe((data) => this.myUnifiedBillings$.next(data));
    return result$.asObservable();
  }

  public cancelDeletionUb(storageAccountId: string): Observable<any> {
    return this.http.put<SwitchCommercial>(`${this.api.v1}/${storageAccountId}/CancelDeletion`, null);
  }

  canSwitchToPaidMode(storageId: string): Observable<boolean> {
    return this.http.get<{ allowed: boolean }>(`${this.api.v1}/ub/CanSwitchToPaidMode`).pipe(map((m) => m.allowed));
  }

  getCompanies(): Observable<CompanyType[]> {
    return this.http.get<CompanyType[]>(`${this.api.legacy}/GetProvidersCompanies`);
  }

  getStorageAccount(id: string, fromWizard = false): Observable<StorageAccountV2> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<StorageAccountV2>(`${path}/${id}`);
  }

  getStorageAccounts(
    params: QueryPagingParams & {
      search?: string;
      orderBy?: string;
    }
  ): Observable<PagedResponse<StorageAccountV2ListUnit>> {
    return this.http.get<PagedResponse<StorageAccountV2ListUnit>>(`${this.api.v2}`, {
      params: omitBy(params, (param) => isNil(param) || param === '')
    });
  }

  getStorageAccountDestinations(
    id: string,
    params?: QueryPagingParams & {
      search?: string;
      orderBy?: string;
      immutabilityEnabled?: boolean;
    }
  ): Observable<DestinationsResponse> {
    return this.http.get<DestinationsResponse>(`${this.api.v2}/${id}/destinations`, {
      params: omitBy(params || {}, (param) => isNil(param) || param === '')
    });
  }

  getStorageAccountRegions({
    storageAccountId,
    googleStorageClass,
    fromWizard
  }: {
    storageAccountId: string;
    googleStorageClass?: string;
    fromWizard?: boolean;
  }): Observable<StorageAccountRegion[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;
    const params: {
      googleStorageClass?: string;
    } = {};

    if (googleStorageClass) {
      params.googleStorageClass = googleStorageClass;
    }

    return this.http.get<StorageAccountRegion[]>(`${path}/${storageAccountId}/regions`, {
      params
    });
  }

  getGoogleStorageClasses(fromWizard = false): Observable<GoogleStorageClass[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<GoogleStorageClass[]>(`${path}/google/classes`);
  }

  getGoogleSubregions(region: string, googleStorageClass: string, fromWizard = false): Observable<StorageAccountRegion[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<[]>(`${path}/google/subregions`, {
      params: {
        region,
        googleStorageClass
      }
    });
  }

  getBucketsNames({
    storageAccountId,
    fromWizard = false,
    skipError = false
  }: {
    storageAccountId: string;
    fromWizard: boolean;
    skipError: boolean;
  }): Observable<{ bucketName: string }[]> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<{ bucketName: string }[]>(`${path}/${storageAccountId}/buckets`, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  getBucketInfo(storageAccountId: string, bucketName: string, fromWizard = false): Observable<BucketInfo> {
    const path = fromWizard ? this.api.wizard : this.api.v2;

    return this.http.get<BucketInfo>(`${path}/${storageAccountId}/buckets/${bucketName}/`, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  createDestination({
    storageAccountId,
    data,
    skipError
  }: {
    storageAccountId: string;
    data: SetDestinationType;
    skipError?: boolean;
  }): Observable<null | { extensions: { [key: number]: string }; instance: string }> {
    return this.http.post<null>(`${this.api.v2}/${storageAccountId}/destinations`, data, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  editDestination({
    storageAccountId,
    data,
    destinationId,
    skipError
  }: {
    storageAccountId: string;
    data: SetDestinationType;
    skipError?: boolean;
    destinationId: string;
  }): Observable<null> {
    return this.http.put<null>(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`, data, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  getDestination(storageAccountId: string, destinationId: string): Observable<Destination> {
    return this.http.get<Destination>(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`);
  }

  deleteDestination(storageAccountId: string, destinationId: string) {
    return this.http.delete(`${this.api.v2}/${storageAccountId}/destinations/${destinationId}`);
  }

  setDestinationS3LifecycleRules(accountId: string, destinationId: string, data: S3LifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/destinations/${destinationId}/lifecyclerule/amazon`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setDestinationAzureLifecycleRules(accountId: string, destinationId: string, data: AzureLifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/destinations/${destinationId}/lifecyclerule/azure`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setStorageAccountAzureLifecycleRules(accountId: string, data: AzureLifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/lifecyclerule/azure`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  setStorageAccountS3LifecycleRules(accountId: string, data: S3LifecycleRulesRequest): Observable<null> {
    return this.http.post<null>(`${this.api.v2}/${accountId}/lifecyclerule/amazon`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  getStoragesAvailableForStorageCreation(): Observable<StoragesAvailableForCreation> {
    return this.http.get<StoragesAvailableForCreation>(`${this.api.wizard}/types`);
  }

  validateStorageWizard({ payload, id, skipError }: { payload: ValidateRequestBody; id: string; skipError?: boolean }) {
    return this.http.put<ValidateResponse>(`${this.api.wizard}/${id}/validate`, payload, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  validateStorage(id: string, skipError: boolean) {
    return this.http.put<ValidateResponse>(`${this.api.v2}/${id}/validate`, null, {
      headers: skipError ? { MANUAL_ERROR_HANDLED } : {}
    });
  }

  create(id: string, data: SetStorageType): Observable<PartialSuccessResponse | null> {
    return this.http.post<PartialSuccessResponse | null>(`${this.api.wizard}/${id}`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  edit(id: string, data: SetStorageType): Observable<PartialSuccessResponse | null> {
    return this.http.put<PartialSuccessResponse | null>(`${this.api.wizard}/${id}`, data, {
      headers: { MANUAL_ERROR_HANDLED }
    });
  }

  deleteV2(id: string, params: { deleteWithData: boolean; code?: string; redirectUri?: string }): Observable<null> {
    return this.http.delete<null>(`${this.api.v2}/${id}`, {
      params
    });
  }

  cancelDeletion(id: string): Observable<null> {
    return this.http.post<null>(`/api/v2/StorageAccounts/${id}/cancel-deletion`, {});
  }

  downloadUbDetails(accountId: string, { startDate, endDate }: { startDate: string; endDate: string }): void {
    window.open(`/api/v2/StorageAccounts/export/${accountId}?startDate=${startDate}&endDate=${endDate}`, '_blank');
  }

  exportStorageAccountsToCsv(fields: string[], orderBy?: string, search?: string) {
    const params = new URLSearchParams(fields.map((param) => ['fields', param]));
    orderBy && params.append('orderBy', orderBy);
    search && params.append('search', search);

    window.open(`/api/v2/StorageAccounts/export?${params.toString()}`, '_blank');
  }
}
